import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getSmartEmojiForLabel } from './InteractiveGraph';
import { resolveFontFamily } from '../utils/fontFamilyResolver';
import { groupSegmentsByRanges } from '../utils/furigana';
import { useFurigana } from '../contexts/FuriganaContext';
import FuriganaText, { renderFuriganaSegments } from './FuriganaText';

// The problem text is UPLOADED RESEARCH STIMULUS. The engine is inherently stimulus-safe, but keyword
// highlighting must never re-tokenize a keyword fragment (箱の**中** in isolation → the whole-label ちゅう
// instead of contextual なか). So the whole stem/question is annotated ONCE and its segments are projected
// onto the keyword spans (groupSegmentsByRanges): a keyword that lands inside a multi-kanji segment (数値 +
// keyword 値) highlights that whole segment rather than vanishing.

const GENERIC_KEYWORD_EMOJIS = new Set(['🔹', '🔸', '🔭']);

const resolveKeywordEmoji = (text) => {
  if (!text) return null;
  const emoji = getSmartEmojiForLabel(text);
  if (!emoji || GENERIC_KEYWORD_EMOJIS.has(emoji)) {
    return null;
  }
  return emoji;
};

const escapeRegex = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const buildLooseKeywordRegex = (keyword) => {
  const trimmed = keyword.trim();
  if (!trimmed) return null;
  const parts = trimmed.split(/\s+/);
  const pattern = parts
    .map((part) => part.split('').map(escapeRegex).join('\\s*'))
    .join('\\s*');
  return new RegExp(pattern);
};

const findKeywordMatch = (text, keyword, startIndex = 0) => {
  if (!text || !keyword) return null;
  const directIndex = text.indexOf(keyword, startIndex);
  if (directIndex >= 0) {
    return { index: directIndex, length: keyword.length };
  }
  const regex = buildLooseKeywordRegex(keyword);
  if (!regex) return null;
  const slice = text.slice(startIndex);
  const match = regex.exec(slice);
  if (!match || typeof match.index !== 'number') return null;
  return { index: startIndex + match.index, length: match[0].length };
};

// Case-insensitive highlight check: handles mismatches between graph labels and keyword text
const isKeywordHighlighted = (highlightedKeywords, keywordText) => {
  if (!Array.isArray(highlightedKeywords) || !keywordText) return false;
  const lowerText = keywordText.toLowerCase();
  return highlightedKeywords.some((kw) => kw && kw.toLowerCase() === lowerText);
};

const toRgba = (color, alpha) => {
  if (!color || typeof color !== 'string') return `rgba(250, 204, 21, ${alpha})`;
  const normalized = color.trim();
  if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(normalized)) {
    const hex = normalized.slice(1);
    const expanded = hex.length === 3
      ? hex.split('').map((char) => char + char).join('')
      : hex;
    const value = parseInt(expanded, 16);
    const r = (value >> 16) & 255;
    const g = (value >> 8) & 255;
    const b = value & 255;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  return normalized;
};

function HighlightableKeyword({ keyword, displayText, renderedContent, isHighlighted, onClick, highlightColor, textColor, typographyStyle }) {
  const { t } = useTranslation();
  const spanRef = useRef(null);
  const prevHighlightedRef = useRef(false);
  const emoji = resolveKeywordEmoji(keyword?.text || '');
  const labelText = displayText ?? keyword?.text ?? '';

  // Auto-scroll into view when highlighted from reverse direction (graph → text)
  useEffect(() => {
    if (isHighlighted && !prevHighlightedRef.current && spanRef.current) {
      spanRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    }
    prevHighlightedRef.current = isHighlighted;
  }, [isHighlighted]);

  const fire = (event) => {
    // Stop propagation to prevent parent question container's onClick from clearing highlights
    event?.stopPropagation?.();
    const rect = spanRef.current?.getBoundingClientRect?.();
    onClick?.(keyword, rect || null);
  };

  return (
    <span
      ref={spanRef}
      className={[
        'cursor-pointer px-1 rounded transition-all duration-300',
        isHighlighted
          ? 'border-b-4 keyword-text-blink'
          : 'border-b border-dashed'
      ].join(' ')}
      style={{
        ...typographyStyle,
        ...(isHighlighted
          ? {
              backgroundColor: toRgba(highlightColor, 0.22),
              borderColor: highlightColor,
              color: textColor,
              borderBottomWidth: '4px',
              borderBottomStyle: 'solid',
              boxShadow: `0 0 0 2px ${toRgba(highlightColor, 0.18)}`,
            }
          : {
              borderColor: toRgba(highlightColor, 0.7),
              backgroundColor: 'transparent',
              color: textColor,
            }
        )
      }}
      onClick={fire}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          fire();
        }
      }}
      role="button"
      tabIndex={0}
      aria-label={t('problemPanel.keywordAria', { text: keyword?.text || '' })}
    >
      {/* renderedContent = pre-computed segments sliced from the whole-stimulus annotation (correct
          context readings). Fall back to FuriganaText only when no projection was supplied. */}
      {renderedContent != null ? renderedContent : <FuriganaText text={labelText} />}
      {emoji && (
        <span className="ml-1" aria-hidden="true" style={{ color: textColor }}>
          {emoji}
        </span>
      )}
    </span>
  );
}

function renderTextWithKeywords({ text, keywords, highlightedKeywords, onKeywordClick, highlightColor, textColor, typographyStyle, furiganaEnabled }) {
  if (!text) return null;
  const safeKeywords = Array.isArray(keywords) ? keywords.filter((kw) => kw?.text) : [];

  // non-overlapping keyword ranges (char offsets), in document order
  const sortedKeywords = [...safeKeywords].sort((a, b) => {
    const aIndex = findKeywordMatch(text, a.text, 0)?.index ?? Number.MAX_SAFE_INTEGER;
    const bIndex = findKeywordMatch(text, b.text, 0)?.index ?? Number.MAX_SAFE_INTEGER;
    return aIndex - bIndex;
  });
  const ranges = [];
  let cursor = 0;
  sortedKeywords.forEach((keyword) => {
    const match = findKeywordMatch(text, keyword.text, cursor);
    if (!match) return;
    ranges.push({ start: match.index, end: match.index + match.length, meta: keyword });
    cursor = match.index + match.length;
  });

  const keywordSpan = (keyword, content, key) => (
    <HighlightableKeyword
      key={key}
      keyword={keyword}
      displayText={keyword.text}
      renderedContent={content}
      isHighlighted={isKeywordHighlighted(highlightedKeywords, keyword.text)}
      onClick={onKeywordClick}
      highlightColor={highlightColor}
      textColor={textColor}
      typographyStyle={typographyStyle}
    />
  );

  // Toggle OFF → raw text, keyword spans by char-slice (no annotation).
  if (!furiganaEnabled) {
    if (!ranges.length) return <span>{text}</span>;
    const parts = [];
    let c = 0;
    ranges.forEach((r, i) => {
      if (r.start > c) parts.push(<span key={`t${i}`}>{text.slice(c, r.start)}</span>);
      parts.push(keywordSpan(r.meta, text.slice(r.start, r.end), `kw${i}`));
      c = r.end;
    });
    if (c < text.length) parts.push(<span key="te">{text.slice(c)}</span>);
    return parts;
  }

  // Toggle ON → annotate the WHOLE stimulus once, project its segments onto the keyword spans.
  return groupSegmentsByRanges(text, ranges).map((g, i) => (g.meta
    ? keywordSpan(g.meta, renderFuriganaSegments(g.segs, `k${i}`), `kw${i}`)
    : <span key={`t${i}`}>{renderFuriganaSegments(g.segs, `t${i}`)}</span>));
}

// Phase A (instrumentation flag only): a per-question answer box. The answer is
// recorded for the researcher; correctness is never shown here (feedback would
// change the intervention). Rendered only when the parent passes onAnswerSubmit.
function AnswerEntry({ question, onSubmit, typographyStyle }) {
  const { t } = useTranslation();
  const [value, setValue] = useState('');
  const [status, setStatus] = useState('idle');
  const valueRef = useRef(value);
  valueRef.current = value;
  const inputId = `research-answer-${question?.id || 'q'}`;

  const submit = async () => {
    const text = value.trim();
    if (!text || status === 'saving') return;
    setStatus('saving');
    const submittedRaw = value;
    try {
      const ok = await onSubmit(question, text);
      if (ok === false) {
        setStatus('error');
        return;
      }
      // Only clear (and say "saved") if the box still holds what was submitted; a
      // newer answer typed while saving is kept and stays unsaved (Codex B-R3).
      if (valueRef.current === submittedRaw) {
        setValue('');
        setStatus('saved');
      } else {
        setStatus('idle');
      }
    } catch (e) {
      setStatus('error');
    }
  };

  return (
    // The question card is itself a button (click/Enter/Space select it), so the
    // answer area must stop those events from reaching it — otherwise typing a
    // space or pressing Enter would re-select the question instead of typing.
    <div
      className="mt-3 flex flex-wrap items-center gap-2"
      data-research-answer-entry={question?.id || ''}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <label htmlFor={inputId} className="text-sm font-semibold" style={{ color: 'var(--text-muted)' }}>
        <FuriganaText text={t('research.answerLabel')} />
      </label>
      <input
        id={inputId}
        type="text"
        maxLength={500}
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
          if (status !== 'saving') setStatus('idle');
        }}
        onKeyDown={(e) => {
          // IME: the Enter that confirms a kana/kanji conversion must not submit.
          if (e.key === 'Enter' && !e.nativeEvent?.isComposing && e.keyCode !== 229) {
            e.preventDefault();
            submit();
          }
        }}
        placeholder={t('research.answerPlaceholder')}
        className="min-w-[8rem] flex-1 rounded-xl border px-3 py-2"
        style={{ ...typographyStyle, borderColor: 'var(--border)', backgroundColor: 'var(--bg-surface)' }}
      />
      <button
        type="button"
        onClick={submit}
        disabled={!value.trim() || status === 'saving'}
        className="rounded-xl border px-4 py-2 text-sm font-semibold disabled:opacity-50"
        style={{ borderColor: 'var(--primary)', color: 'var(--primary)', backgroundColor: 'var(--bg-surface)' }}
      >
        <FuriganaText text={t('research.answerSubmit')} />
      </button>
      <span role="status" aria-live="polite" className="text-xs" style={{ color: status === 'error' ? 'var(--danger, #b91c1c)' : 'var(--text-muted)' }}>
        {status === 'saved' && <FuriganaText text={t('research.answerRecorded')} />}
        {status === 'error' && <FuriganaText text={t('research.answerError')} />}
      </span>
    </div>
  );
}

function ProblemTextPanel({
  stem,
  stemKeywords,
  questions,
  activeQuestion,
  highlightedKeywords,
  settings,
  onQuestionSelect,
  onKeywordClick,
  // Phase A research props — all absent unless the instrumentation flag is on,
  // in which case the panel renders exactly as before.
  onAnswerSubmit = null,
  linkInvitationVisible = false,
  onLinkInvitationDismiss = null,
  onPanelRevisit = null,
  // Identity of the task on screen: the revisit window starts over for each task (Codex B-R9).
  revisitScope = null,
}) {
  const { t } = useTranslation();
  const { enabled: furiganaEnabled } = useFurigana();
  const panelRef = useRef(null);
  const revisitRef = useRef(onPanelRevisit);
  // Published after commit (Codex B-R7).
  useLayoutEffect(() => {
    revisitRef.current = onPanelRevisit;
  });

  // Phase A: "設問パネル再訪" by scrolling — the panel leaves the viewport and comes
  // back after at least 2 s (stacked layouts on tablets put it above the chart).
  // Observation only; nothing on screen changes.
  const observeRevisits = Boolean(onPanelRevisit);
  useEffect(() => {
    const node = panelRef.current;
    if (!observeRevisits || !node || typeof IntersectionObserver === 'undefined') return undefined;
    let hiddenAt = null;
    let active = true; // a late notification of a replaced observer is ignored
    const io = new IntersectionObserver((entries) => {
      if (!active) return;
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          if (hiddenAt == null) hiddenAt = Date.now();
          return;
        }
        if (hiddenAt != null && Date.now() - hiddenAt >= 2000 && revisitRef.current) {
          revisitRef.current('scroll_into_view');
        }
        hiddenAt = null;
      });
    }, { threshold: 0.25 });
    io.observe(node);
    return () => {
      active = false;
      io.disconnect();
    };
  }, [observeRevisits, revisitScope]);
  const safeQuestions = useMemo(() => (Array.isArray(questions) ? questions : []), [questions]);
  const highlightColor = settings?.problemTextHighlightColor || settings?.highlightAccentColor || '#facc15';
  const textColor = settings?.textColor || 'var(--text)';
  const typographyStyle = useMemo(() => ({
    fontFamily: resolveFontFamily(settings?.fontFamily),
    fontSize: `${settings?.fontSize || 16}px`,
    letterSpacing: `${settings?.letterSpacing ?? 0.05}em`,
    color: textColor,
  }), [settings?.fontFamily, settings?.fontSize, settings?.letterSpacing, textColor]);

  return (
    <div ref={panelRef} className="rounded-3xl border p-5" style={{ borderColor: 'var(--border)', backgroundColor: 'var(--bg-surface)', boxShadow: 'var(--shadow-sm)' }}>
      {linkInvitationVisible && (
        <div
          className="mb-3 flex items-start justify-between gap-3 rounded-2xl border p-3 text-sm"
          role="note"
          data-research-link-invitation="true"
          style={{ borderColor: highlightColor, backgroundColor: toRgba(highlightColor, 0.12), color: textColor }}
        >
          <span style={typographyStyle}><FuriganaText text={t('research.invitationText')} /></span>
          <button
            type="button"
            onClick={() => onLinkInvitationDismiss?.()}
            className="shrink-0 rounded-full border px-3 py-1 text-xs font-semibold"
            style={{ borderColor: 'var(--border)', backgroundColor: 'var(--bg-surface)', color: 'var(--text)' }}
          >
            <FuriganaText text={t('research.invitationDismiss')} />
          </button>
        </div>
      )}
      <div className="rounded-2xl border p-4" style={{ borderColor: 'var(--border)', backgroundColor: 'var(--bg-muted)' }}>
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold" style={{ color: 'var(--text-muted)' }}><FuriganaText text={t('problemPanel.heading')} /></h3>
          <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{t('problemPanel.keywordHint')}</span>
        </div>
        <p className="mt-2 leading-relaxed" style={typographyStyle}>
          {renderTextWithKeywords({
            text: stem || '',
            keywords: stemKeywords,
            highlightedKeywords,
            onKeywordClick,
            furiganaEnabled,
            highlightColor,
            textColor,
            typographyStyle,
          })}
        </p>
      </div>

      <div className="mt-4">
        <h3 className="text-sm font-semibold" style={{ color: 'var(--text-muted)' }}><FuriganaText text={t('problemPanel.questionsHeading')} /></h3>
        <div className="mt-2 space-y-2">
          {safeQuestions.map((q) => {
            const isActive = activeQuestion?.id === q.id;
            return (
              <div
                key={q.id}
                className={[
                  'rounded-2xl border p-4 cursor-pointer transition-all duration-200',
                  isActive
                    ? 'border-transparent'
                    : 'border-transparent'
                ].join(' ')}
                style={isActive
                  ? {
                      borderColor: highlightColor,
                      backgroundColor: toRgba(highlightColor, 0.12),
                      boxShadow: `0 0 0 4px ${toRgba(highlightColor, 0.18)}`,
                    }
                  : { backgroundColor: 'var(--bg-surface)' }
                }
                onClick={() => onQuestionSelect?.(q)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onQuestionSelect?.(q);
                  }
                }}
              >
                <div className="flex items-start gap-3">
                  <span className="mt-0.5 inline-flex h-7 w-12 items-center justify-center rounded-full text-xs font-semibold" style={{ backgroundColor: isActive ? toRgba(highlightColor, 0.18) : 'var(--bg-muted)', color: textColor }}>
                    {q.number || q.id}
                  </span>
                  <div className="flex-1 leading-relaxed" style={typographyStyle}>
                    {renderTextWithKeywords({
                      text: q.text || '',
                      keywords: q.keywords,
                      highlightedKeywords,
                      onKeywordClick: (keyword, rect) => onKeywordClick?.(keyword, rect, { question: q }),
                      furiganaEnabled,
                      highlightColor,
                      textColor,
                      typographyStyle,
                    })}
                    {isActive && onAnswerSubmit && (
                      <AnswerEntry
                        key={`answer-${q.id}`}
                        question={q}
                        onSubmit={onAnswerSubmit}
                        typographyStyle={typographyStyle}
                      />
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          {safeQuestions.length === 0 && (
            <div className="rounded-2xl border border-dashed p-6 text-sm" style={{ borderColor: 'var(--border)', backgroundColor: 'var(--bg-surface)', color: 'var(--text-muted)' }}>
              <FuriganaText text={t('problemPanel.noQuestions')} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProblemTextPanel;
